import { Card } from "@mui/material";
import * as styles from "./PermissionManage.module.scss";
import React, { Suspense, useEffect, useState } from "react";
import {
  LoadingSpinner,
  Accordion,
  Search,
} from "cprime-foundational-components";
import DragablePermissionElement from "./DragablePermissionElement";
import PermissionElement from "./PermissionElement";
import PermissionSidePanel from "./PermissionSidePanel";
import PermissionPanelContent from "./PermissionPanelContent";
import {
  deletePermission,
  getPermissionList,
  reorderPermissionList,
  setPermissionStatus,
} from "../../services/RequestAccess/RequestAccess";
import { useParams } from "react-router-dom";
import { triggerToast } from "../../utils/toast";
import NoAvailablePermission from "./NoAvailablePermission";

const updateList = (list: any[], excludeId: string) =>
  list?.filter((per: any) => per?.permission_id !== excludeId) || [];

const sortByRank = (a: any, b: any) => a?.rank - b?.rank;

const filteredTasks = (data: any, searchString: any) => {
  return data?.filter((item: any) =>
    item["permission_name"]
      ?.toLowerCase()
      ?.includes(searchString?.toLowerCase()),
  );
};

const initialExpandedPanelsState = [
  "Available Permissions",
  "Unavailable Permissions",
];

function PermissionManage({
  title,
  openPanel,
  setOpenPanel,
  getApplicationDetails,
  isEditable,
}: any) {
  const scssClass = styles as any;
  const { id } = useParams();

  const [searchString, setSearchString] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<Array<string>>(
    initialExpandedPanelsState,
  );
  const [tasks, setTasks] = useState<any>(() => {
    const initialTasks = {
      permission: [],
      unavailable: [],
    };
    return initialTasks;
  });

  const [activeCard, setActiveCard] = useState<string | null>(null);

  const onDrop = async (position: number) => {
    if (!activeCard) return;
    const [type, indexStr] = activeCard.split("-");
    const list = type === "permission" ? tasks?.permission : tasks?.unavailable;
    const task = list?.[+indexStr];
    if (!task) return;

    const isDraggingUp = +indexStr > position;
    const adjustedPosition = isDraggingUp
      ? position
      : Math.max(position - 1, 0);
    const newPosition = list?.[adjustedPosition];

    const applicationId = id?.split("-")?.[1];
    if (
      !applicationId ||
      !task?.permission_id ||
      !newPosition?.rank ||
      newPosition?.rank === task?.rank
    )
      return;

    setIsLoading(true);
    try {
      const res = await reorderPermissionList({
        app_id: applicationId,
        moves: [
          {
            permission_id: task?.permission_id,
            old_rank: task?.rank,
            new_rank: newPosition?.rank,
          },
        ],
      });
      if (res?.status !== "SUCCESS") {
        triggerToast(
          res?.error || res?.message || "Failed to update rank",
          "error",
        );
        return;
      }

      const updatedData = res?.data || [];
      setTasks({
        ...tasks,
        permission: updatedData.filter(
          (rec: any) => rec?.status === "available",
        ),
        unavailable: updatedData.filter(
          (rec: any) => rec?.status === "unavailable",
        ),
      });
      triggerToast(res?.message, res?.error ? "error" : "success");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccordionOnChange = (value: string) => {
    if (expandedPanels?.includes(value)) {
      setExpandedPanels(expandedPanels?.filter((d) => d !== value));
    } else {
      setExpandedPanels([...expandedPanels, value]);
    }
  };

  const filteredPermissions = React.useMemo(
    () => filteredTasks(tasks?.permission, searchString),
    [tasks, searchString],
  );

  const filteredUnavailable = React.useMemo(
    () => filteredTasks(tasks?.unavailable, searchString),
    [tasks, searchString],
  );

  const getPermission = async () => {
    const applicationId = id?.split("-")?.[1];
    if (applicationId) {
      setIsLoading(true);
      try {
        const res = await getPermissionList(applicationId);
        if (res?.status === "SUCCESS") {
          setTasks({
            ...tasks,
            permission: res?.data?.available,
            unavailable: res?.data?.unavailable,
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const setPermissionUpdate = async (
    permission: any,
    status: boolean,
    cb?: () => void,
  ) => {
    const applicationId = id?.split("-")?.[1];
    if (!applicationId || !permission?.permission_id) return;
    const res = await setPermissionStatus({
      app_id: applicationId,
      permission_id: permission?.permission_id,
      status: status ? "available" : "unavailable",
    });

    if (res?.status !== "SUCCESS") {
      triggerToast(
        res?.error ? res.message : "Failed to update status.",
        "error",
      );
      getApplicationDetails?.();
      return;
    }

    const updatedPermission = {
      ...permission,
      status: status ? "available" : "unavailable",
    };

    const permissionList = updateList(
      tasks?.permission,
      permission.permission_id,
    );
    const unavailableList = updateList(
      tasks?.unavailable,
      permission.permission_id,
    );

    if (status) {
      permissionList.push(updatedPermission);
    } else {
      unavailableList.push(updatedPermission);
    }
    setTasks({
      ...tasks,
      permission: permissionList.sort(sortByRank),
      unavailable: unavailableList.sort(sortByRank),
    });

    triggerToast(res.message, res.error ? "error" : "success");
    cb?.();
  };

  const deletePermissionConfirm = async (permission: any) => {
    const applicationId = id?.split("-")?.[1];
    if (applicationId && isEditable) {
      const res = await deletePermission({
        app_id: applicationId,
        permission_id: permission?.permission_id,
      });
      if (res?.status === "SUCCESS") {
        const newPermission = tasks?.permission?.filter(
          (per: any) => per?.permission_id !== permission?.permission_id,
        );
        const newUnavailable = tasks?.unavailable?.filter(
          (per: any) => per?.permission_id !== permission?.permission_id,
        );
        setTasks({
          ...tasks,
          permission: newPermission,
          unavailable: newUnavailable,
        });
        const toastType = res?.error ? "error" : "success";
        triggerToast(res?.message, toastType);
      } else {
        triggerToast(
          res?.error ? res?.message : "Failed to Delete Permission.",
          "error",
        );
      }
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        maxHeight: `calc(100vh - ${!isEditable ? 370 : 336}px)`,
        overflow: "auto",
        marginBottom: "1rem",
      }}
    >
      {isLoading ? (
        <LoadingSpinner fullScreen={true} />
      ) : (
        <Card variant="outlined" className={scssClass?.card}>
          <div className={scssClass?.cardheader}>
            <span className={scssClass?.cardtitle}>{title}</span>
            <Search
              placeholder="Search Permission Description"
              autoFocus={true}
              value={searchString}
              onChange={(e) => setSearchString(e)}
              onSubmit={() => {}}
              onClear={() => setSearchString("")}
              sx={{
                height: "40px",
                width: "318px",
                input: { fontFamily: "ToyotaType-Regular" },
              }}
            />
          </div>
          <div>
            {[...initialExpandedPanelsState]?.map((sectionName) => {
              return (
                <Accordion
                  key={sectionName}
                  title={sectionName}
                  // panel={sectionName}
                  sx={{
                    width: "100%",
                    fontSize: "16px",
                    fontWeight: "400",
                    boxShadow: "unset",
                    marginTop: "0px !important",
                    border: "none",
                    ".MuiTypography-body1": {
                      p: {
                        fontSize: "16px",
                        fontWeight: "400",
                        fontFamily: "ToyotaType-Regular",
                      },
                    },
                    ".expand-more-icon": {
                      width: "24px",
                      height: "100%",
                    },
                  }}
                  expanded={expandedPanels?.includes(sectionName)}
                  onChange={() => handleAccordionOnChange(sectionName)}
                >
                  <div data-testid={`${sectionName}Permissions`}>
                    {sectionName === initialExpandedPanelsState[0] ? (
                      filteredPermissions?.length > 0 ? (
                        filteredPermissions?.map((d: any, index: any) => (
                          <Suspense key={d?.permission_id}>
                            <div data-testid={index} key={d?.permission_id}>
                              {index === 0 && (
                                <DragablePermissionElement
                                  onDrop={(p: number) => onDrop(0)}
                                  type="permission"
                                  item={activeCard}
                                  index={index}
                                />
                              )}
                              <PermissionElement
                                permission={d}
                                setActiveCard={(eleIndex: any) => {
                                  if (eleIndex || eleIndex === 0) {
                                    setActiveCard(`permission-${index}`);
                                  } else {
                                    setActiveCard("");
                                  }
                                }}
                                index={index}
                                label={d?.permission_name}
                                isActive={isEditable}
                                isAvailable={d?.status === "available"}
                                isEditable={isEditable}
                                isEditAllow={isEditable}
                                isDeletable={false}
                                isArchive={true}
                                isUnarchive={true}
                                deletePermissionConfirm={
                                  deletePermissionConfirm
                                }
                                setPermissionUpdate={(
                                  permission: any,
                                  status: boolean,
                                  cb?: any,
                                ) =>
                                  setPermissionUpdate(permission, status, cb)
                                }
                                getPermission={getPermission}
                              />
                              <DragablePermissionElement
                                onDrop={(p: number) => onDrop(index + 1)}
                                type="permission"
                                item={activeCard}
                                index={index}
                              />
                            </div>
                          </Suspense>
                        ))
                      ) : (
                        <NoAvailablePermission type="Available Permissions" />
                      )
                    ) : filteredUnavailable?.length > 0 ? (
                      filteredUnavailable?.map((d: any, index: any) => (
                        <Suspense key={d?.permission_id || d?.name}>
                          <div data-testid={index}>
                            {index === 0 && (
                              <DragablePermissionElement
                                onDrop={(p: number) => onDrop(0)}
                                type="unavailable"
                                item={activeCard}
                                index={index}
                              />
                            )}
                            <PermissionElement
                              permission={d}
                              setActiveCard={(eleIndex: any) => {
                                if (eleIndex || eleIndex === 0) {
                                  setActiveCard(`unavailable-${index}`);
                                } else {
                                  setActiveCard("");
                                }
                              }}
                              index={index}
                              label={d?.permission_name}
                              isActive={isEditable}
                              isAvailable={d?.status === "available"}
                              isEditable={isEditable}
                              isEditAllow={isEditable}
                              isDeletable={true}
                              canDelete={isEditable}
                              distributorname={d?.distributor}
                              setPermissionUpdate={(
                                permission: any,
                                status: boolean,
                                cb?: any,
                              ) => setPermissionUpdate(permission, status, cb)}
                              deletePermissionConfirm={deletePermissionConfirm}
                              getPermission={getPermission}
                            />
                            <DragablePermissionElement
                              onDrop={(p: number) => onDrop(index + 1)}
                              type="unavailable"
                              item={activeCard}
                              index={index}
                            />
                          </div>
                        </Suspense>
                      ))
                    ) : (
                      <NoAvailablePermission type="Unavailable Permissions" />
                    )}
                  </div>
                </Accordion>
              );
            })}
            {/* )} */}
          </div>
        </Card>
      )}
      {openPanel && (
        <PermissionSidePanel
          isEdit={false}
          isAvailable={false}
          onClose={() => setOpenPanel(false)}
          getPermission={getPermission}
          getApplicationDetails={getApplicationDetails}
          BodyComponent={<PermissionPanelContent isEdit={false} />}
        />
      )}
    </div>
  );
}

export default PermissionManage;
